/**
 * Types describing the surface that originated a session. Backs the ADO
 * Agency × Mission Control crossover (see
 * docs/agency-mission-control-crossover/process.md §7.4).
 *
 * Everything here is additive and optional on the existing Task / Session
 * shapes. When absent the UI treats a session as a first-party GitHub Copilot
 * "cloud" session, matching today's behavior.
 */

import type {APITaskArtifact} from './artifact'
import {COPILOT_CLI_SLUG, COPILOT_JETBRAINS_SLUG, COPILOT_VSCODE_SLUG} from '../utils/agent-helpers'

/** Where the session physically runs / was created from. */
export type SessionSource = 'cloud' | 'local' | 'agency'

/**
 * Identifies the surface that originated a session. `source` is normally
 * derived (see getEffectiveSource) — this optional override lets the backend
 * stamp it authoritatively if it ever supplies an origin envelope. When
 * absent, source resolves to 'cloud'.
 */
export interface SessionOrigin {
  source?: SessionSource
}

/**
 * Source label/copy lookup. Centralized so badges, filter chips, and menu
 * items stay in sync. Keep the labels short — they ride in dense task rows.
 */
export const SOURCE_LABELS: Record<SessionSource, string> = {
  cloud: 'Cloud',
  local: 'Local',
  agency: 'Agency',
}

export const SOURCE_DESCRIPTIONS: Record<SessionSource, string> = {
  cloud: 'Runs on GitHub-managed compute.',
  local: 'Runs on a user-managed device — Copilot CLI or an IDE-embedded client (VS Code / JetBrains).',
  agency: 'Originated from ADO Agency Hub.',
}

/**
 * Resolve the effective source for a record. Resolution order:
 *
 *   1. Explicit `origin.source` (when the backend supplies one — the source
 *      of truth)
 *   2. Presence of an ADO artifact in `task.artifacts[]` — derives "agency"
 *      without waiting for the backend to populate `origin.source` (artifacts
 *      ship today via the sessionstore PR; origin.source does not yet)
 *   3. `task.agentSlug` — derives "local" for known CLI/IDE agent slugs
 *      (copilot-developer-cli, vscode-chat, jetbrains-chat)
 *   4. Fallback to "cloud" — preserves today's no-op behavior when none of
 *      the above apply, so existing production responses render unchanged
 *
 * Pass `null` / `undefined` (or no second arg) for the legacy
 * origin-only lookup.
 */
export function getEffectiveSource(
  origin: SessionOrigin | undefined | null,
  task?: {agentSlug?: string; artifacts?: APITaskArtifact[] | null} | null,
): SessionSource {
  if (origin?.source) return origin.source
  if (task?.artifacts && hasAdoArtifact(task.artifacts)) return 'agency'
  return deriveSourceFromAgentSlug(task?.agentSlug)
}

/**
 * Agent slugs that identify client-side ("local") agent runtimes — Copilot
 * CLI on a user's machine, or the IDE-embedded clients (VS Code / JetBrains).
 * Reuses the canonical slug constants from `utils/agent-helpers` (the same
 * ones `AGENT_INFO_REGISTRY` is keyed by) rather than redeclaring the string
 * literals, so this set can't drift from the registry. Imported from
 * `agent-helpers` specifically (not `agents/agent-profile`) to keep this pure
 * type module free of the React-heavy profile components.
 */
const LOCAL_AGENT_SLUGS: ReadonlySet<string> = new Set([COPILOT_CLI_SLUG, COPILOT_VSCODE_SLUG, COPILOT_JETBRAINS_SLUG])

export function deriveSourceFromAgentSlug(agentSlug: string | undefined | null): SessionSource {
  if (agentSlug && LOCAL_AGENT_SLUGS.has(agentSlug)) return 'local'
  return 'cloud'
}

/**
 * True when the artifact list contains any entry from the ADO provider. The
 * canonical signal for "this session is Agency-originated" once the backend
 * stops sending `origin.source` for these tasks (which it does not today —
 * the artifact entry is shipped, the origin envelope is not).
 */
export function hasAdoArtifact(artifacts: readonly APITaskArtifact[] | null | undefined): boolean {
  if (!artifacts) return false
  return artifacts.some(a => a.provider === 'azure_devops')
}

/**
 * Returns the canonical ADO pull-request URL for a task, if one was attached
 * via `Task.SetAzureDevOpsPullRequestArtifact` server-side. Returns undefined
 * when the task has no ADO PR artifact (the common case for first-party
 * tasks).
 *
 * The backend stores ADO resources as URL-only payloads — no parsed
 * org / project / pipeline / run fields. Callers that need richer detail
 * (e.g. for a "View in Azure DevOps" popover header) should parse the URL
 * client-side; the URL pattern is stable:
 *   https://dev.azure.com/{org}/{project}/_git/{repo}/pullrequest/{id}
 */
export function getAdoPullRequestUrl(
  task: {artifacts?: APITaskArtifact[] | null} | null | undefined,
): string | undefined {
  if (!task?.artifacts) return undefined
  for (const artifact of task.artifacts) {
    if (
      artifact.provider === 'azure_devops' &&
      artifact.type === 'azure_devops_resource' &&
      typeof artifact.data?.url === 'string' &&
      artifact.data.url.length > 0
    ) {
      return artifact.data.url
    }
  }
  return undefined
}

/**
 * Whether mid-session steering should be suppressed for a given source.
 *
 * Agency sessions are surfaced in Mission Control but steering is not yet wired
 * end-to-end through Agency → copilotd, so we hide the steer affordance for them
 * to avoid offering a control that does nothing. Remove this once Agency
 * steering is supported.
 */
export function isSteeringSuppressed(source: SessionSource): boolean {
  return source === 'agency'
}

export type ChannelOrigin = 'Slack' | 'Microsoft Teams'

export function getChannelOrigin(eventType: string | null | undefined): ChannelOrigin | null {
  if (eventType === 'slack') return 'Slack'
  if (eventType === 'teams') return 'Microsoft Teams'
  return null
}
